"use client"

import { cartProductDetails, type CartProduct } from "@/lib/services/cart-product-details"
import { lastCheckoutProductDetails } from "@/lib/services/last-checkout-details"
import type { CatalogGateway } from "@/platform/api/gateways/catalog"
import { getRegion } from "@/platform/catalog/region-action"
import type { LastCheckoutDetailsResult } from "@/platform/shopper/carts.client"

export const productLookup = (id: string, handle?: string) => (handle ? { handle } : { id })

export const getBrowserProductPricing = async (catalog: CatalogGateway, productId: string, regionId: string, handle?: string, publicOnly = false) => {
  const [product, discounts] = await Promise.all([catalog.product({ ...productLookup(productId, handle), regionId, publicOnly }), catalog.discounts([productId], publicOnly)])
  return { product, discounts }
}

export const getBrowserLastCheckoutProductDetails = async (
  catalog: CatalogGateway,
  {
    id,
    variantId,
    countryCode,
  }: {
    id: string
    variantId: string
    countryCode: string
  }
): Promise<LastCheckoutDetailsResult> => {
  try {
    const region = await getRegion(countryCode)
    if (!region) return { status: "error" }

    const { product, discounts } = await getBrowserProductPricing(catalog, id, region.id)
    if (!product?.id) return { status: "error" }

    const variant = product.variants?.find((v) => v.id === variantId)
    if (!variant) return { status: "unavailable" }

    return await lastCheckoutProductDetails(product, variant, region, discounts)
  } catch {
    return { status: "error" }
  }
}

export const getBrowserCartProductDetails = async (
  catalog: CatalogGateway,
  {
    id,
    variantId,
    countryCode,
  }: {
    id: string
    variantId: string
    countryCode: string
  }
): Promise<CartProduct | null> => {
  const region = await getRegion(countryCode)
  if (!region) throw new Error("Cart product requires a region")

  const product = await catalog.product({ id, regionId: region.id })
  if (!product?.id) throw new Error("Cart product was not found")

  return cartProductDetails(product, variantId)
}
